import { useEditorStore } from '@/lib/editor/editorStore';
import { EDITING_TOOLS, type EditingToolName } from './tools';
import { VIDEO_FILTERS } from '@/lib/editor/filters';
import { getEffectPreset } from '@/lib/editor/effects';
import { MUSIC_LIBRARY } from '@/lib/editor/musicLibrary';
import { OVERLAY_TEMPLATES } from '@/lib/editor/overlayTemplates';
import { STICKER_PACKS } from '@/lib/editor/stickerPacks';

export interface ToolApplyResult { ok: boolean; summary: string }

const fail = (summary: string): ToolApplyResult => ({ ok: false, summary });
const ok = (summary: string): ToolApplyResult => ({ ok: true, summary });

export function applyEditingToolCall(name: string, input: unknown): ToolApplyResult {
  if (!Object.prototype.hasOwnProperty.call(EDITING_TOOLS, name)) return fail(`Неизвестный инструмент: ${name}`);
  const tool = (EDITING_TOOLS as Record<string, { inputSchema: { safeParse: (v: unknown) => { success: boolean; data?: any } } }>)[name];
  if (!tool) return fail(`Неизвестный инструмент: ${name}`);
  const parsed = tool.inputSchema.safeParse(input);
  if (!parsed.success) return fail(`Некорректные аргументы для ${name}`);
  const args = parsed.data;
  const store = useEditorStore.getState();

  switch (name as EditingToolName) {
    case 'addClip': {
      const item = store.library.find((m) => m.id === args.mediaId);
      if (!item) return fail(`В библиотеке нет медиа ${args.mediaId}`);
      store.addClip(item);
      return ok(`Добавил клип «${item.title}»`);
    }
    case 'addClipSegment': {
      const item = store.library.find((media) => media.id === args.mediaId);
      if (!item) return fail(`В библиотеке нет медиа ${args.mediaId}`);
      const sourceDuration = item.duration ?? Number.POSITIVE_INFINITY;
      const startSec = Math.min(Math.max(args.startSec, 0), Math.max(0, sourceDuration - 0.5));
      const endSec = Math.min(Math.max(args.endSec, startSec + 0.5), sourceDuration);
      store.addClip(item);
      const addedClipId = useEditorStore.getState().selectedClipId;
      if (!addedClipId) return fail('Не удалось добавить найденный фрагмент');
      useEditorStore.getState().updateClip(addedClipId, {
        mediaOffset: startSec,
        duration: Math.max(0.5, endSec - startSec),
        isTrimmed: true,
      });
      return ok(`Добавил фрагмент «${item.title}» ${startSec.toFixed(1)}–${endSec.toFixed(1)}с`);
    }
    case 'removeClip': {
      if (!store.clips.some((c) => c.id === args.clipId)) return fail('Клип не найден');
      store.removeClip(args.clipId);
      return ok('Удалил клип');
    }
    case 'duplicateClip': {
      if (!store.clips.some((c) => c.id === args.clipId)) return fail('Клип не найден');
      store.duplicateClip(args.clipId);
      return ok('Продублировал клип');
    }
    case 'trimClip': {
      if (!store.clips.some((c) => c.id === args.clipId)) return fail('Клип не найден');
      if (args.edge === 'in') store.trimClipIn(args.clipId, args.deltaSeconds);
      else store.trimClipOut(args.clipId, args.deltaSeconds);
      return ok(`Подрезал клип (${args.edge}) на ${args.deltaSeconds}с`);
    }
    case 'splitClip': {
      store.splitClipAtPlayhead();
      return ok('Разрезал клип в позиции плейхеда');
    }
    case 'reorderClip': {
      const from = store.clips.findIndex((c) => c.id === args.clipId);
      if (from < 0) return fail('Клип не найден');
      store.reorderClips(from, args.toIndex);
      return ok(`Переместил клип на позицию ${args.toIndex + 1}`);
    }
    case 'updateClipProperties': {
      if (store.trackSettings.video.locked) return fail('Видеодорожка заблокирована');
      if (!store.clips.some((clip) => clip.id === args.clipId)) return fail('Клип не найден');
      if (args.filterId && !VIDEO_FILTERS.some((filter) => filter.id === args.filterId)) {
        return fail(`Неизвестный фильтр: ${args.filterId}`);
      }
      const { clipId, ...patch } = args;
      store.updateClip(clipId, patch);
      return ok('Обновил свойства клипа');
    }
    case 'setTrackState': {
      const { track, ...patch } = args;
      store.setTrackSettings(track, patch);
      return ok(`Обновил дорожку ${track}`);
    }
    case 'setClipTransition': {
      const index = store.clips.findIndex((clip) => clip.id === args.clipId);
      if (index < 0) return fail('Клип не найден');
      if (index === 0) return fail('У первого клипа нет предыдущего клипа для перехода');
      store.setTransition(args.clipId, { type: args.type, durationSec: args.durationSec });
      return ok(`Поставил переход между клипами: ${args.type}, ${args.durationSec}с`);
    }
    case 'removeClipTransition': {
      const clip = store.clips.find((item) => item.id === args.clipId);
      if (!clip) return fail('Клип не найден');
      store.removeTransition(args.clipId);
      return ok('Удалил переход между клипами');
    }
    case 'addText': {
      store.addTextLayer();
      const layers = useEditorStore.getState().layers;
      const layer = layers[layers.length - 1];
      if (layer) {
        useEditorStore.getState().updateLayer(layer.id, {
          text: args.text,
          ...(args.x != null ? { x: args.x } : {}),
          ...(args.y != null ? { y: args.y } : {}),
          ...(args.size != null ? { size: args.size } : {}),
          ...(args.color != null ? { color: args.color, textColor: args.color } : {}),
        });
      }
      return ok(`Добавил текст «${args.text}»`);
    }
    case 'addCaption': {
      if (store.trackSettings.overlays.locked) return fail('Дорожка слоёв заблокирована');
      store.addCaptionLayer();
      const layerId = useEditorStore.getState().selectedLayerId;
      if (!layerId) return fail('Не удалось добавить субтитр');
      useEditorStore.getState().updateLayer(layerId, {
        text: args.text,
        label: args.text,
        ...(args.start != null ? { start: args.start } : {}),
        ...(args.end != null ? { end: args.end } : {}),
        ...(args.x != null ? { x: args.x } : {}),
        ...(args.y != null ? { y: args.y } : {}),
        ...(args.size != null ? { size: args.size } : {}),
        ...(args.color != null ? { color: args.color, textColor: args.color } : {}),
      });
      return ok(`Добавил субтитр «${args.text}»`);
    }
    case 'addSticker': {
      if (store.trackSettings.overlays.locked) return fail('Дорожка слоёв заблокирована');
      const sticker = STICKER_PACKS.flatMap((pack) => pack.stickers).find((item) => item.id === args.stickerId);
      if (!sticker) return fail('Стикер не найден');
      store.addStickerLayer(sticker);
      const layerId = useEditorStore.getState().selectedLayerId;
      if (!layerId) return fail('Не удалось добавить стикер');
      useEditorStore.getState().updateLayer(layerId, {
        ...(args.x != null ? { x: args.x } : {}),
        ...(args.y != null ? { y: args.y } : {}),
        ...(args.widthPercent != null ? { widthPercent: args.widthPercent } : {}),
        ...(args.start != null ? { start: args.start } : {}),
        ...(args.end != null ? { end: args.end } : {}),
      });
      return ok(`Добавил стикер «${sticker.title}»`);
    }
    case 'applyTemplate': {
      if (store.trackSettings.overlays.locked) return fail('Дорожка слоёв заблокирована');
      const template = OVERLAY_TEMPLATES.find((item) => item.id === args.templateId);
      if (!template) return fail('Шаблон не найден');
      store.applyOverlayTemplate(template);
      return ok(`Применил шаблон «${template.name}»`);
    }
    case 'applyEffect': {
      store.addEffectLayer(args.effectSrc, args.label);
      return ok(`Применил эффект «${args.label}»`);
    }
    case 'addCatalogEffect': {
      const effect = getEffectPreset(args.effectId);
      store.addEffectLayer(effect.id, effect.name);
      return ok(`Применил эффект «${effect.name}»`);
    }
    case 'addMusic': {
      if (store.trackSettings.music.locked) return fail('Музыкальная дорожка заблокирована');
      const track = MUSIC_LIBRARY.find((item) => item.id === args.trackId);
      if (!track) return fail('Музыка не найдена');
      store.addAudioTrack({
        src: track.src,
        fileName: track.title,
        duration: track.duration,
        start: args.start,
        volume: args.volume ?? 0.5,
        fadeIn: args.fadeIn ?? 0.4,
        fadeOut: args.fadeOut ?? 0.5,
        ducking: args.ducking ?? true,
      });
      return ok(`Добавил музыку «${track.title}»`);
    }
    case 'updateAudioTrack': {
      if (store.trackSettings.music.locked) return fail('Музыкальная дорожка заблокирована');
      if (!store.audioTracks.some((track) => track.id === args.trackId)) return fail('Музыкальная дорожка не найдена');
      const { trackId, ...patch } = args;
      store.updateAudioTrack(trackId, patch);
      return ok('Обновил музыкальную дорожку');
    }
    case 'removeAudioTrack': {
      if (!store.audioTracks.some((track) => track.id === args.trackId)) return fail('Музыкальная дорожка не найдена');
      store.removeAudioTrack(args.trackId);
      return ok('Удалил музыкальную дорожку');
    }
    case 'setAspectRatio': {
      store.setAspectRatio(args.aspectRatio, true);
      return ok(`Формат кадра: ${args.aspectRatio}`);
    }
    case 'setCrossfade': {
      store.setCrossfade(args.seconds);
      return ok(`Кроссфейд: ${args.seconds}с`);
    }
    case 'updateLayer': {
      if (!store.layers.some((l) => l.id === args.layerId)) return fail('Слой не найден');
      const { layerId, color, ...rest } = args;
      store.updateLayer(layerId, { ...rest, ...(color != null ? { color, textColor: color } : {}) });
      return ok('Обновил слой');
    }
    case 'setLayerTransition': {
      if (!store.layers.some((layer) => layer.id === args.layerId)) return fail('Слой не найден');
      const transition = { type: args.type, durationSec: args.durationSec };
      store.updateLayer(args.layerId, args.edge === 'in' ? { transitionIn: transition } : { transitionOut: transition });
      return ok(`Добавил ${args.edge === 'in' ? 'появление' : 'исчезновение'} слоя`);
    }
    case 'addLayerKeyframe': {
      if (!store.layers.some((layer) => layer.id === args.layerId)) return fail('Слой не найден');
      const { layerId, time, ...visualPatch } = args;
      if (Object.keys(visualPatch).length > 0) store.updateLayer(layerId, visualPatch);
      store.addLayerKeyframe(layerId, time);
      return ok(`Добавил ключевой кадр слоя на ${time.toFixed(1)}с`);
    }
    case 'removeLayer': {
      if (!store.layers.some((l) => l.id === args.layerId)) return fail('Слой не найден');
      store.removeLayer(args.layerId);
      return ok('Удалил слой');
    }
    default:
      return fail(`Инструмент ${name} не поддержан`);
  }
}
